import { CheckCircle2 } from 'lucide-react';
import type { NoteData, NoteField, NoteType } from '@/api/notes';

// Read-only renderer for a session note. Used in two places:
//   1. SignNoteDialog — parent reviews the note before signing.
//   2. ViewNoteDialog — parent re-reads a Finalized/Complete note from
//      their session history.
//
// The parent portal must not leak any PHI the staff-side NoteForm would
// hide: we render the saved field values, the session notes column, and
// signer names/roles. We do NOT re-render captured signature bitmaps —
// showing them would add no value and widens the PHI footprint.

interface Props {
    noteType: NoteType | null;
    noteData: NoteData | null;
    /** Free-text `notes` column on the session row — separate from
     *  structured note_data fields. */
    sessionNotes?: string;
}

export function NoteReadOnlyView({ noteType, noteData, sessionNotes }: Props) {
    const fieldValues = noteData?.fields ?? {};
    const sigValues = noteData?.signatures ?? {};

    if (!noteType) {
        return (
            <div className="text-sm text-text-subtle italic">
                {sessionNotes
                    ? <p className="whitespace-pre-wrap not-italic text-text">{sessionNotes}</p>
                    : 'No note content available.'}
            </div>
        );
    }

    const hasFields = noteType.fields.length > 0;
    const hasSignatures = noteType.signatures.length > 0;

    return (
        <div className="space-y-4 text-sm">
            {hasFields && (
                <div className="space-y-3">
                    {noteType.fields.map((f) => (
                        <FieldRow key={f.id} field={f} value={fieldValues[f.id]} />
                    ))}
                </div>
            )}

            {sessionNotes && (
                <div>
                    <div className="text-xs font-medium text-text-subtle mb-1">Session notes</div>
                    <p className="whitespace-pre-wrap text-text">{sessionNotes}</p>
                </div>
            )}

            {hasSignatures && (
                <div className="pt-3 border-t border-border space-y-2">
                    {noteType.signatures.map((s) => {
                        const v = sigValues[s.id];
                        const signed = !!(v?.drawnPng && v.signedByName);
                        return (
                            <div key={s.id} className="flex items-start justify-between gap-3">
                                <div className="text-xs text-text-subtle">{s.label}</div>
                                <div className="text-xs text-right min-w-0">
                                    {signed ? (
                                        <div className="flex items-center gap-1 text-text">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-success-text" />
                                            <span className="font-medium truncate">{v.signedByName}</span>
                                            {v.role && (
                                                <span className="text-text-muted">· {v.role}</span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-text-muted italic">Not yet signed</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {!hasFields && !sessionNotes && !hasSignatures && (
                <div className="text-text-subtle italic">No note content available.</div>
            )}
        </div>
    );
}

function FieldRow({ field, value }: { field: NoteField; value: unknown }) {
    return (
        <div>
            <div className="text-xs font-medium text-text-subtle mb-0.5">{field.label}</div>
            <div className="text-text">{formatValue(field, value)}</div>
        </div>
    );
}

function formatValue(field: NoteField, value: unknown): React.ReactNode {
    if (field.type === 'checkbox') {
        return <span>{value === true ? 'Yes' : 'No'}</span>;
    }
    if (field.type === 'multiselect') {
        const list = Array.isArray(value) ? value.map(String).filter(Boolean) : [];
        if (list.length === 0) return <span className="text-text-subtle italic">—</span>;
        return <span>{list.join(', ')}</span>;
    }
    if (typeof value === 'string' && value.trim()) {
        if (field.type === 'textarea') {
            return <p className="whitespace-pre-wrap">{value}</p>;
        }
        return <span>{value}</span>;
    }
    if (typeof value === 'number') return <span>{value}</span>;
    return <span className="text-text-subtle italic">—</span>;
}
